import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getProfile, updateProfile } from '@/lib/services/profiles'
import type { Profile as ProfileType } from '@/lib/types'
import { Modal, Input, Alert } from '@/components/ui'
import { useTranslation } from '@/contexts/TranslationContext'
import { formatPhone } from '@/lib/deviceDetection'

interface BillingProfileModalProps {
  isOpen: boolean
  onClose: () => void
}

export function BillingProfileModal({ isOpen, onClose }: BillingProfileModalProps) {
  const { t } = useTranslation()
  const { user: authUser } = useAuth()
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState<ProfileType | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  const [fullName, setFullName] = useState('')
  const [whatsapp, setWhatsapp] = useState('')

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' ? window.innerWidth < 768 : false)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Load profile when modal opens
  useEffect(() => {
    if (!isOpen || !authUser?.id) return
    
    const loadProfile = async () => {
      setLoading(true)
      setError(null)
      setSuccess(null)

      try {
        const userProfile = await getProfile(authUser.id)
        if (userProfile) {
          setProfile(userProfile)
          setFullName(userProfile.full_name || '')
          setWhatsapp(userProfile.whatsapp || userProfile.phone || '')
        }
      } catch (err: any) {
        console.error('Error loading profile:', err)
        setError(t('profile.loadError'))
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [isOpen, authUser?.id, t])

  const handleSave = async () => {
    setError(null)
    setSuccess(null)

    if (!fullName.trim()) {
      setError(t('auth.errorNameRequired'))
      return
    }

    if (!authUser?.id) {
      setError(t('profile.loadError'))
      return
    }

    setSaving(true)

    try {
      await updateProfile(authUser.id, {
        full_name: fullName.trim(),
        whatsapp: whatsapp ? formatPhone(whatsapp) : undefined,
      })

      setSuccess(t('profile.updateSuccess'))
      // Reload profile
      const userProfile = await getProfile(authUser.id)
      if (userProfile) {
        setProfile(userProfile)
      }
      // Close modal after success
      setTimeout(() => onClose(), 1500)
    } catch (err: any) {
      console.error('Error updating profile:', err)
      setError(t('profile.updateError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={t('profile.title')}
      size="sm"
      headerLeft={
        isMobile ? (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label={t('actions.save')}
            title={t('profile.save')}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          {!isMobile && (
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
              aria-label={t('actions.close')}
              title={t('actions.cancel')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      }
    >
      {error && <Alert variant="error" className="mb-4">{error}</Alert>}
      {success && <Alert variant="success" className="mb-4">{success}</Alert>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
        </div>
      ) : (
        <div className="space-y-4">
          <Input
            id="fullName"
            type="text"
            label={t('auth.fullName')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t('auth.fullName')}
            disabled={saving}
            required
          />

          <Input
            id="whatsapp"
            type="tel"
            label={t('auth.whatsapp') || 'WhatsApp'}
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder={t('auth.whatsapp') || 'WhatsApp'}
            disabled={saving}
          />
        </div>
      )}
    </Modal>
  )
}
